/**
 * @file useListingUpload.js
 * @brief Handles create and edit submission logic for marketplace listings.
 */
import { useState, useCallback } from "react";
import { FIELD_LIMITS, stripHtmlTags } from "../constants/fieldLimits";

/**
 * Validate and submit listing create or update requests.
 * @param {Object} params Hook parameters.
 * @param {Object} params.api API client used for listing requests.
 * @param {Function} params.navigate React Router navigation callback.
 * @param {boolean} params.isEditMode Whether the form edits an existing listing.
 * @param {string} params.listingId Listing identifier used in edit mode.
 * @param {Function} params.t Translation helper.
 * @returns {{saving: boolean, errors: Object, setErrors: Function, submitListing: Function}} Submission state and handlers.
 */
export function useListingUpload({ api, navigate, isEditMode, listingId, t }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const submitListing = useCallback(async ({ event, statusValue, payload }) => {
        event.preventDefault();

        if ((payload.title || "").length > FIELD_LIMITS.listingTitle) {
            setErrors({ title: [t("listingForm.titleTooLong", { max: FIELD_LIMITS.listingTitle })] });
            return;
        }

        if (stripHtmlTags(payload.description || "").trim().length > FIELD_LIMITS.listingDescription) {
            setErrors({ description: [t("listingForm.descriptionTooLong", { max: FIELD_LIMITS.listingDescription })] });
            return;
        }

        setSaving(true);
        setErrors({});

        try {
            const formData = new FormData();
            formData.append("title", payload.title);
            formData.append("description", payload.description);
            formData.append("price", payload.price);
            formData.append("category_id", payload.category?.value ?? "");
            formData.append("location_id", payload.location?.value ?? "");
            formData.append("status", statusValue);

            if (isEditMode) {
                const updateImages = payload.images.map((img, index) =>
                    img.existing
                        ? { id: img.imageId, number: index }
                        : { file_key: `file-${index}`, number: index }
                );
                formData.append("update_images", JSON.stringify(updateImages));

                payload.images.forEach((img, index) => {
                    if (!img.existing) {
                        formData.append(`file-${index}`, img.file);
                    }
                });

                await api.patch(`listings/${listingId}/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                navigate(-1);
                return;
            }

            payload.images.forEach((img) => formData.append("new_images", img.file));
            await api.post("listings/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            navigate("/account/listings");
        } catch (err) {
            if (err.response?.status === 400) {
                setErrors(err.response.data);
            } else {
                console.error(isEditMode ? "Update failed:" : "Create listing failed:", err);
            }
        } finally {
            setSaving(false);
        }
    }, [api, isEditMode, listingId, navigate, t]);

    return { saving, errors, setErrors, submitListing };
}
